import styles from './BookingCalendar.module.css';

import type { FC } from 'react';
import type { Dayjs } from 'dayjs';
import type { ApiRentalOccupiedRange } from '../../types/rental';

import dayjs from 'dayjs';
import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';

import { isDayOccupied, toOccupiedInterval } from 'src/utils/rentalOccupancy';

import { Typography } from 'src/components/themed/Typography';

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

interface BookingCalendarProps {
  /** Occupied windows `[startDate, endDate)` from the listing detail (rental + inspection tail). */
  occupiedRanges: ApiRentalOccupiedRange[];
  /** Selected rental start, 'YYYY-MM-DD' or ''. */
  startDate: string;
  /** Selected rental end (return day), 'YYYY-MM-DD' or ''. */
  endDate: string;
  onSelect: (startDate: string, endDate: string) => void;
}

/**
 * Month-grid range picker for the booking form. Days inside an occupied window
 * are visually disabled; tap once for the start day, tap a later day for the
 * end (return) day, tap an earlier/occupied day to restart the selection.
 */
export const BookingCalendar: FC<BookingCalendarProps> = ({ occupiedRanges, startDate, endDate, onSelect }) => {
  const today = dayjs().startOf('day');
  const [month, setMonth] = useState(() => today.startOf('month'));

  // Occupied intervals as dayjs bounds (`end === null` = open-ended defect hold),
  // ready for overlap checks.
  const ranges = useMemo(() => occupiedRanges.map(toOccupiedInterval), [occupiedRanges]);

  const selStart = startDate ? dayjs(startDate).startOf('day') : null;
  const selEnd = endDate ? dayjs(endDate).startOf('day') : null;

  // A day is occupied when its [day, day+1) interval overlaps any occupied window.
  const isOccupied = (day: Dayjs) => isDayOccupied(day, ranges);

  const handleDayClick = (day: Dayjs) => {
    if (day.isBefore(today) || isOccupied(day)) return;

    const value = day.format('YYYY-MM-DD');

    // No start yet, restarting after a full range, or tapping at/before the
    // current start -> begin a new selection.
    if (!selStart || selEnd || !day.isAfter(selStart)) {
      onSelect(value, '');
      return;
    }

    onSelect(startDate, value);
  };

  // Leading offset of the 1st (Monday-first grid) + all days of the month.
  const firstOfMonth = month.startOf('month');
  const leadingEmpty = (firstOfMonth.day() + 6) % 7;
  const daysInMonth = month.daysInMonth();

  const cells: (Dayjs | null)[] = [
    ...Array.from({ length: leadingEmpty }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => firstOfMonth.add(i, 'day')),
  ];

  const canGoPrev = month.isAfter(today.startOf('month'));

  return (
    <div className={styles.calendar}>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.navBtn}
          onClick={() => setMonth(month.subtract(1, 'month'))}
          disabled={!canGoPrev}
          aria-label="Previous month"
        >
          <CaretLeft size={16} color="white" />
        </button>
        <Typography color="white" fontWeight="600">
          {month.format('MMMM YYYY')}
        </Typography>
        <button
          type="button"
          className={styles.navBtn}
          onClick={() => setMonth(month.add(1, 'month'))}
          aria-label="Next month"
        >
          <CaretRight size={16} color="white" />
        </button>
      </div>

      <div className={styles.grid}>
        {WEEKDAYS.map((wd) => (
          <div key={wd} className={styles.weekday}>
            {wd}
          </div>
        ))}
        {cells.map((day, index) => {
          if (!day) {
             
            return <div key={`empty-${index}`} />;
          }

          const occupied = isOccupied(day);
          const past = day.isBefore(today);
          const isStart = !!selStart && day.isSame(selStart, 'day');
          const isEnd = !!selEnd && day.isSame(selEnd, 'day');
          const inRange = !!selStart && !!selEnd && day.isAfter(selStart) && day.isBefore(selEnd);

          return (
            <button
              type="button"
              key={day.format('YYYY-MM-DD')}
              className={classNames(styles.day, {
                [styles.dayPast]: past,
                [styles.dayOccupied]: occupied && !past,
                [styles.dayEdge]: isStart || isEnd,
                [styles.dayInRange]: inRange,
              })}
              onClick={() => handleDayClick(day)}
              disabled={past || occupied}
            >
              {day.date()}
            </button>
          );
        })}
      </div>

      <div className={styles.legend}>
        <span className={classNames(styles.legendSwatch, styles.legendOccupied)} />
        <Typography color="lightWhite" variant="small">
          Unavailable (booked or under post-return inspection)
        </Typography>
      </div>
    </div>
  );
};
